<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { T } from '@tolgee/vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { GameSound, useSounds } from '@/composables/game/sounds'
import Collect2xImage from '@/assets/images/prize-cards/collect2x.webp'
import Collect6xImage from '@/assets/images/prize-cards/collect6x.webp'
import { computed, ref, watch } from 'vue'
import { AD_TYPE, useAd } from '@/composables/game/ad'
import { useConfig } from '@/composables/game/config'
import { useEcommercePrice } from '@/composables/game/ecommerce'
import PrizeCard from '@/components/PrizeCard.vue'
import { CARD_COLORS } from '@/enums/common'
import { type EcommerceItem, SPECIAL_ECOMMERCE_ITEM_ID } from '@/types/shared/player-config'
import ShopItemDetails from '@/components/ShopItemDetails.vue'
import api from '@/services/api'
import { env } from '@/env'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'

const props = defineProps<{
	show: boolean
	profit: number
	offlineProfitHours: number
}>()
const emit = defineEmits(['hide'])

const { addHapticFeedback, addNotificationFeedback } = useHapticFeedback()
const { playSound } = useSounds()
const {
  showAd,
  watchedDailyAdsCount,
  maxDailyAdsCount,
  adWatchedOfflineProfitMultiplier,
} = useAd(AD_TYPE.FOR_OFFLINE_PROFIT)
const {
  maxDailyAdsCount: forSpinMaxDailyAdsCount,
  watchedDailyAdsCount: forSpinWatchedDailyAdsCount,
} = useAd(AD_TYPE.FOR_SPIN)
const { config } = useConfig()
const { getStarPrice } = useEcommercePrice()

const allAdsWatched = computed(() => watchedDailyAdsCount.value >= maxDailyAdsCount.value || forSpinWatchedDailyAdsCount.value >= forSpinMaxDailyAdsCount.value)

const ecommerceItemX2 = computed(() => {
  const mustItem = config.value.game.ecommerce.items.find(item => item.id === SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X2)
  if (!mustItem) {
    api.reportError({
      code: 'ecommerceItemNotFound',
      description: `Ecommerce item with the id of ${SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X2} not found in backend list`,
      metadata: {},
    })
    throw new Error('Ecommerce item not found')
  }
  return mustItem
})
const ecommerceItemX6 = computed(() => {
  const mustItem = config.value.game.ecommerce.items.find(item => item.id === SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X6)
  if (!mustItem) {
    api.reportError({
      code: 'ecommerceItemNotFound',
      description: `Ecommerce item with the id of ${SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X6} not found in backend list`,
      metadata: {},
    })
    throw new Error('Ecommerce item not found')
  }
  return mustItem
})

const selectedEcommerceItem = ref<EcommerceItem | null>(null)

const usdAmountX2 = computed(() => getStarPrice(ecommerceItemX2.value).usdAmount)
const multiplierX2 = computed(() => ecommerceItemX2.value.benefits[0]?.amount ?? 1)
const multipliedProfitX2 = computed(() => props.profit * multiplierX2.value)

const usdAmountX6 = computed(() => getStarPrice(ecommerceItemX6.value).usdAmount)
const multiplierX6 = computed(() => ecommerceItemX6.value.benefits[0]?.amount ?? 1)
const multipliedProfitX6 = computed(() => props.profit * multiplierX6.value)

const ecommerceImageMap: Record<string, string> = {
  [SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X2]: Collect2xImage,
  [SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X6]: Collect6xImage,
}

const canShowCards = computed(() => props.offlineProfitHours >= (env.app.isProd ? 2 : 0.1))
const loadingWatchAd = ref<boolean>(false)

async function onWatchAd() {
  loadingWatchAd.value = true
  const watched = await showAd()
  loadingWatchAd.value = false
  if (watched) {
    emit('hide')
  }
}

watch(() => props.show, show => {
  if (show) {
    selectedEcommerceItem.value = null
  }
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    :back-button="!!selectedEcommerceItem"
    @hide="$emit('hide')"
    @back="selectedEcommerceItem = null"
  >
    <ShopItemDetails
      v-if="selectedEcommerceItem"
      :image="ecommerceImageMap[selectedEcommerceItem.id]"
      :item="selectedEcommerceItem"
      :coins-to-earn="selectedEcommerceItem.id === SPECIAL_ECOMMERCE_ITEM_ID.OFFLINE_PROFIT_X2 ? multipliedProfitX2 : multipliedProfitX6"
      @hide="$emit('hide')"
    />
    <div
      v-else
      class="w-auto flex flex-col items-center relative"
    >
      <img
        :src="CoinIcon"
        class="size-[120px]"
      >
      <NumberFormatTooltip
        :number="profit"
        :formatting-type="'full'"
      >
        <div
          class="mt-4 text-[32px] leading-[40px] font-bold text-light-white"
        >
          {{ formatNumberFullTruncate(profit) }}
        </div>
      </NumberFormatTooltip>
      <div class="max-w-[214px] text-center text-base font-medium text-white/60">
        <T
          key-name="offline_profit.message"
          default-value="You have collected profit while you were away!"
        />
      </div>
      <div
        v-if="canShowCards"
        class="grid grid-cols-2 justify-between gap-3 mt-8 w-full"
      >
        <PrizeCard
          :color="CARD_COLORS.PINK"
          @click="selectedEcommerceItem = ecommerceItemX2"
        >
          <NumberFormatTooltip
            :number="multipliedProfitX2"
            :formatting-type="'full'"
          >
            <div
              class="flex gap-1 items-center mt-6"
            >
              <img
                :src="CoinIcon"
                class="h-6 w-auto object-contain"
              >
              <span
                class="text-[24px] leading-[18px] font-[900] text-white black-stroke disable-slashed-zero overflow-visible"
              >
                {{ formatNumberFullTruncate(multipliedProfitX2) }}
              </span>
            </div>
          </NumberFormatTooltip>
          <div class="w-[100px] mt-5 flex-1">
            <img
              :src="Collect2xImage"
              class="w-full h-auto"
            >
          </div>
          <div
            class="w-full flex rounded-[13px] p-0.5 green-stroke-gradient"
          >
            <BaseButton
              style-type="primary"
              button-class="px-3 h-[48px]"
              class="w-full"
            >
              ${{ formatNumberFullTruncate(usdAmountX2, true) }}
            </BaseButton>
          </div>
        </PrizeCard>

        <PrizeCard
          :color="CARD_COLORS.ORANGE"
          @click="selectedEcommerceItem = ecommerceItemX6"
        >
          <NumberFormatTooltip
            :number="multipliedProfitX6"
            :formatting-type="'full'"
          >
            <div
              class="flex gap-1 items-center mt-6"
            >
              <img
                :src="CoinIcon"
                class="h-6 w-auto object-contain"
              >
              <span
                class="text-[24px] leading-[18px] font-[900] text-white black-stroke disable-slashed-zero overflow-visible"
              >
                {{
                  formatNumberFullTruncate(multipliedProfitX6)
                }}</span>
            </div>
          </NumberFormatTooltip>
          <div class="w-[100px] mt-5 flex-1">
            <img
              :src="Collect6xImage"
              class="w-full h-auto"
            >
          </div>
          <div
            class="w-full flex rounded-[13px] gold-gradient"
          >
            <div
              class="w-full flex rounded-[13px] p-0.5 white-stroke-gradient"
            >
              <BaseButton
                style-type="gold"
                button-class="px-3 h-[48px]"
                class="w-full"
              >
                ${{ formatNumberFullTruncate(usdAmountX6, true) }}
              </BaseButton>
            </div>
          </div>
        </PrizeCard>
      </div>
      <BaseButton
        :style-type="canShowCards ? 'secondary' : 'primary' "
        class="w-full mt-3"
        @click="addHapticFeedback('medium'); addNotificationFeedback('success'); playSound(GameSound.PURCHASE_SUCCESS); $emit('hide');"
      >
        <template #leftIcon>
          <img
            :src="CoinIcon"
            class="w-auto h-full"
          >
        </template>
        <T
          key-name="offline_profit.claim"
          default-value="Claim"
        />
      </BaseButton>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

.second-black-stroke {
	-webkit-text-stroke: 1.6px black;
}

.black-stroke {
	text-shadow: -1px -1px 0 #000,
	1px -1px 0 #000,
	-1px 1px 0 #000,
	1px 1px 0 #000,
	-1.6px 0 0 #000,
	1.6px 0 0 #000,
	0 -1.6px 0 #000,
	0 1.6px 0 #000,
	-0.8px -0.8px 0 #000, /* Finer shadow adjustments */ 0.8px -0.8px 0 #000,
	-0.8px 0.8px 0 #000,
	0.8px 0.8px 0 #000;
}

.icon-stroke {
	stroke: black !important; /* Set the stroke color */
	stroke-width: 1.6px !important; /* Set the stroke thickness */
	stroke-linejoin: round !important; /* Optional: Round the corners of the stroke */
	fill: currentColor !important; /* Keep the fill color of the icon */
}

.green-stroke-gradient {
	background: linear-gradient(to right, rgba(254, 78, 254, 0.75) 0%, rgba(20, 241, 255, 0.75) 33%);
}

.gold-gradient {
	background: linear-gradient(to right,
	#FFF246 0%,
	#E5B80B 37%,
	#FFF571 69%,
	#E5B80B 100%
	);
}

.white-stroke-gradient {
	background: linear-gradient(to right,
	rgba(255, 255, 255, 0.38) 0%,
	rgba(255, 255, 255, 0) 100%
	);
}

</style>
